import { Component, OnInit, Inject, AfterViewInit, OnDestroy, ViewChild, computed, effect, inject, signal } from '@angular/core';

import { Config, IonRouterOutlet, Platform, NavController, ToastController } from '@ionic/angular';
import { registerAppExit } from '@shared/platform/app-exit';
import { isChromelessRoute, swipeBackFor } from '@shared/platform/chromeless-route';
import { OutletRegistry } from '@shared/platform/outlet-registry.service';
import { Router, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Event as RouterEvent, EventType } from '@angular/router';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Store, Select, Actions, ofActionSuccessful } from '@ngxs/store';
//import { ThemeService } from '../shared/services/theme.service';
import { AppState, IAppStateModel } from '@shared/store/app.state';
import { Observable, Subscription, of, BehaviorSubject, combineLatest, map, startWith, take, filter, merge, EMPTY } from 'rxjs';
import { LocationService, PushNotificationRegistrationService, AppShellFacade, PlatformCapabilityService, KzThemeService } from '@shared/services/index';
import { IPage, Page } from '@shared/models/page.model';
import { LanguageService } from '@shared/services/index';
import { NavItem } from '@shared/kz-ui';
import { TranslateService } from '@ngstack/translate';
import { isNullOrUndefined } from '@koomzo/commonutil';
import { IUser, ITenantAppSelection, countryOfPhone } from '@koomzo/commonmodel';
import { LoadTenantAppSelection, SetUserLocation, SetLocalizationSettings } from '@shared/store/app.actions';
import { AuthStateModel } from '@common/auth/store/auth.state';
import { LoggingService, Logger } from 'ionic-logging-service';
import { environment } from '@env/environment';
import { Logout, LogIn, RestoreSession } from '@common/auth/store/auth.actions';
import { IGeoLocationModel } from '@koomzo/coremodel';
import { SwUpdate } from '@angular/service-worker';
import { CapabilityService } from '@shared/services/capability/capability.service';
import { appModuleShown, capabilityShown } from '@shared/services/capability/capability-gate';
import { CapabilityNgxsState } from '@shared/store/capability.state';
import { LoadTenantCapabilities } from '@shared/store/capability.actions';
import { inProviderSpace, sessionRoleFor } from '@common/auth/models/session-role';
import { roleHomeUrl } from '@common/auth/services/space.guards';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AttentionRegistrar } from '@shared/services/attention/attention-registrar.service';
import { AttentionService } from '@shared/services/attention/attention.service';
import { attentionDisplay } from '@shared/services/attention/attention.models';
import { moduleIdFor } from '@shared/services/capability/module-id-map';
import { ModuleId } from '@shared/services/capability/capability.models';
import { KZ_PERF_ON, kzMark } from '@shared/util/kz-perf';
import { afterFirstNavigation } from '@shared/util/after-first-navigation';
import { AppBuildGateService } from '@core/update-gate/app-build-gate.service';

(window as any).google2 = 'google2';

/** The perf trace's router marks (`localStorage['kz.perf']`): each event's name, with its url or lazy path. */
const MARKED_ROUTER_EVENTS: Partial<Record<EventType, string>> = {
  [EventType.NavigationStart]: 'NavigationStart',
  [EventType.RouteConfigLoadStart]: 'RouteConfigLoadStart',
  [EventType.RouteConfigLoadEnd]: 'RouteConfigLoadEnd',
  [EventType.GuardsCheckStart]: 'GuardsCheckStart',
  [EventType.GuardsCheckEnd]: 'GuardsCheckEnd',
  [EventType.ActivationEnd]: 'ActivationEnd',
  [EventType.NavigationEnd]: 'NavigationEnd',
  [EventType.NavigationCancel]: 'NavigationCancel',
  [EventType.NavigationError]: 'NavigationError',
};
function markRouterEvent(e: RouterEvent): void {
  const name = MARKED_ROUTER_EVENTS[(e as { type?: EventType }).type as EventType];
  if (!name) {
    return;
  }
  const ev = e as { url?: string; route?: { path?: string }; snapshot?: { routeConfig?: { path?: string } } };
  kzMark(`router:${name}`, ev.url ?? ev.route?.path ?? ev.snapshot?.routeConfig?.path);
}

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {


  get currentUser(): IUser {
    const authState = this.store.selectSnapshot<AuthStateModel>(state => state.authState);
    return !isNullOrUndefined(authState) ? authState.user : null;
  }

  pages$: Observable<IPage[]>;
  /** Design-system nav-rail items, mapped from pages$ and kept reactive to language changes. */
  navItems$: Observable<NavItem[]>;
  private pagesSubject = new BehaviorSubject<IPage[]>([]);
 // showSplash = true;
  backButtonSubscription: Subscription;
  /** The shell's outlet — the app exits on hardware back only when it cannot go back (kzd r1 §16 item 26). */
  @ViewChild(IonRouterOutlet) routerOutlet?: IonRouterOutlet;
  /** The nested outlets (the provider shell's) — the back-button exit asks the topmost (recheck N1). */
  private readonly outlets = inject(OutletRegistry);
  /** Rebuilds the nav whenever the tenant's app selection loads/changes (install-gate). */
  private menuSubscription?: Subscription;
  /** kz2 F2: the capability gate the nav reads (capability-gate.ts). */
  private readonly capability = inject(CapabilityService);
  /** kz2 M1: the modules' attention badges (D158) — inventory's are the first wired in. */
  private readonly attention = inject(AttentionService);
  private readonly attentionCounters$ = toObservable(this.attention.counters);

  /**
   * kz2 M3: a route whose data says `chromeless` renders without the app's rail — the restaurant's
   * customer-facing kiosk (D70, D72): on an unattended screen the rail is a live operator work-count
   * leak and a tap away from the floor plan and Setup for any member of the public. Staff leave it
   * by the kiosk shell's long-press.
   */
  private readonly chromeRouter = inject(Router);
  readonly chromeless = toSignal(
    this.chromeRouter.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      /* The one rule, shared with the provider shell's nested outlet (`chromeless-route.ts`). */
      map(() => isChromelessRoute(this.chromeRouter.routerState.snapshot.root)),
    ),
    { initialValue: false },
  );

  /**
   * kzd-ac AC-R6: this Android, iOS or desktop build is below the minimum — "Update required" covers the app, the sign-in
   * page included, and makes everything else in `ion-app` inert itself, overlays included (MV-4). Nothing underneath
   * stops: it only takes no new work. Never on the web, never on the customer display.
   */
  readonly updateRequired = inject(AppBuildGateService).blocked;

  /** The root outlet's swipe-back: Ionic's default (iOS mode only), and off on a chromeless screen (REVIEW-INT N-1). */
  private readonly ionicConfig = inject(Config);
  readonly swipeBack = computed(() => swipeBackFor(this.chromeless(), this.ionicConfig));

  /**
   * A navigation in flight — its guards, a lazy module's chunk, the tenant's first reads — shows
   * the shell's loading indicator (kz2 M7 dev check: a typed-in deep link showed a blank page for
   * ~3 s in every module). It appears only after 200 ms, so an in-app hop never flickers.
   */
  readonly routeLoading = signal(false);
  private routeLoadingTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly routeLoadingSub = this.chromeRouter.events.subscribe((e) => {
    if (KZ_PERF_ON) {
      markRouterEvent(e);
    }
    if (e instanceof NavigationStart) {
      if (this.routeLoadingTimer === null) {
        this.routeLoadingTimer = setTimeout(() => this.routeLoading.set(true), 200);
      }
    } else if (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) {
      if (this.routeLoadingTimer !== null) {
        clearTimeout(this.routeLoadingTimer);
        this.routeLoadingTimer = null;
      }
      this.routeLoading.set(false);
    }
  });
  menuTheme: string;
  logger: Logger;  
  // tenantAppSelection$: Observable<ITenantAppSelection[]>;
  // tenantAppSelectionList: ITenantAppSelection[] = [];
  tenantAppSelectionAppCodes: string[] = [];

  constructor(
    private platform: Platform,
    private navCtrl: NavController,
    @Inject('isApp') public isApp,
    public languageService: LanguageService,
    protected store: Store,
    private pushNotificationRegistrationService:PushNotificationRegistrationService,
    loggingService: LoggingService,
    private actions: Actions,
    private locationService:LocationService,
  //  public backgroundMode: BackgroundMode,
  //  public localNotifications: LocalNotifications,
   private swUpdate: SwUpdate,
   private toastCtrl: ToastController,
   private translate: TranslateService,
   private appShellFacade: AppShellFacade,
   private platformCapability: PlatformCapabilityService,
   private kzTheme: KzThemeService,
   private router: Router
  ) {
    kzMark('shell:constructor');
    this.logger = loggingService.getLogger('AppComponent');

    // kz2 M1: drain every module's attention projections once, at start (D158) — a badge is never a
    // property of being on the screen it counts.
    inject(AttentionRegistrar).registerAll();

    // Repaint the status bar whenever the theme changes. Tracking the signal rather than the NGXS
    // ChangeAppTheme action deliberately: KzThemeService is also driven directly (startup restore,
    // the ThemeSwitcherService facade), and the signal covers every one of those entry points.
    effect(() => {
      this.kzTheme.theme();
      this.applyStatusBarStyleFromTheme();
    });

    // kz2 M3: a chromeless route hides the app shell's bottom tabs too (`base-index` reads the class):
    // the kiosk's public screen must not offer the Register, Kitchen and Floor.
    effect(() => document.body.classList.toggle('kz-chromeless', this.chromeless()));

    this.initializeApp();
  }

  @Select(AppState.AppTheme)
  appTheme$: Observable<string>;

  @Select(AppState.AppLang)
  appLang$: Observable<string>;

  async ngOnInit(): Promise<void> {
    kzMark('shell:init');

    // Track which app-module is currently being browsed (drives app-scoped tabs/home later).
    // Behaviour-neutral in P0 — nothing consumes currentAppModuleId yet.
    this.appShellFacade.initRouteTracking();

    // Map role/appMode-filtered pages -> nav-rail items, re-mapping on language change
    // so ILocaleProp titles stay translated (replaces the template's inlinetranslate pipe).
    this.navItems$ = combineLatest([
      this.pagesSubject,
      this.railLang$(),
      // kz2 M1: re-map when a badge moves, so the rail's counts are live.
      this.attentionCounters$,
    ]).pipe(map(([pages, lang]) => this.buildNavItems(pages, lang)));

    //just a trigger to load the tenant app selection
    // this.store.dispatch(new LoadTenantAppSelection({id: this.currentUser.tenantId }));



    // Repopulate the menu after a fresh login OR a session restore on reload.
    this.actions.pipe(ofActionSuccessful(LogIn, RestoreSession)).subscribe(() => this.populateMenu());
    this.actions.pipe(ofActionSuccessful(Logout)).subscribe(() => this.navCtrl.navigateRoot(['/login']));

    // Rehydrate localization on session restore (see rehydrateLocalizationOnRestore), and set it on every sign-in —
    // the owner signup's sign-in included, which never set it (kz2 accounts, PLAN §9 item 10).
    this.actions
      .pipe(ofActionSuccessful(RestoreSession))
      .subscribe(() => this.rehydrateLocalizationOnRestore());
    this.actions
      .pipe(ofActionSuccessful(LogIn))
      .subscribe(() => this.rehydrateLocalizationOnRestore(true));

    // RestoreSession may complete during APP_INITIALIZER (before this subscription
    // is set up). If the user was already restored, populate the menu immediately.
    if (this.currentUser) {
      this.populateMenu();
      this.rehydrateLocalizationOnRestore();
    }

    // if (environment.production) {
    //   const hasUpdate = await this.swUpdate.checkForUpdate();
    //   if (hasUpdate) {
    //     const toast = await this.toastCtrl.create({
    //       message: 'Update available!',
    //       position: 'bottom',
    //       buttons: [
    //         {
    //           text: 'Reload',
    //           role: 'cancel',
    //         },
    //       ],
    //     });
    //     await toast.present();
    //     toast
    //       .onDidDismiss()
    //       .then(() => this.swUpdate.activateUpdate())
    //       .then(() => window.location.reload());
    //   }
    // }
  }


    /**use ionic lifecycle hook to update splitpane state for this specific page */
  // public ionViewWillEnter(): void {

  //      this.actions.pipe(ofActionSuccessful(LogIn)).subscribe(() => {
  //     console.log('ionViewWillEnter::loading menu pages');

  //     const appState = this.store.selectSnapshot<IAppStateModel>(state => state.app);
  //     const tenantAppSelectionList = appState.tenantAppSelection;
  //     this.tenantAppSelectionAppCodes = tenantAppSelectionList?.map(t=>t.appCode) || [];

  //     this.pages$ = this.getMenuPages(this.currentUser);
  //   });

  // }

  /** Last `--safe-area-inset-top` this component wrote, so it can tell its own value from Capacitor's. */
  private androidStatusBarInsetWritten: string | null = null;

  private statusBarRouteSubscription?: Subscription;

  /** Last style handed to the plugin, so repeated sampling passes are no-ops when nothing changed. */
  private lastAppliedStatusBarStyle?: Style;

  /** Bound once so the same reference can be removed in ngOnDestroy. */
  private readonly onModalStatusBarChange = () => this.scheduleStatusBarRestyle(0);

  /**
   * Drive the native status bar icon colour from the design-system token.
   *
   * The header is an `<ion-toolbar color="primary">`, and Ionic puts the safe-area padding on
   * `ion-header ion-toolbar:first-of-type` — i.e. *inside* the toolbar — so the toolbar's own
   * background fills the status bar strip. The system clock and status icons are therefore drawn
   * on top of `--ion-color-primary`, and their colour has to match that, not the OS light/dark
   * setting.
   *
   * `Style.Default` is the trap when the toolbar is what's behind the bar: Capacitor defines it as
   * "based on the device appearance", so on a light-mode phone it resolves to LIGHT ->
   * `setAppearanceLightStatusBars(true)` -> **dark icons on the brand colour**. Read
   * `--ion-color-primary-contrast-rgb` instead — the token that already answers "what colour goes
   * on top of primary?" — so every theme in KZ_THEMES is right by construction and a theme switch
   * repaints the bar. Note the inversion in the plugin's vocabulary: `Style.Dark` means "light text
   * for dark backgrounds", which is what a *light* contrast token calls for.
   *
   * But the toolbar is not always what's behind the bar, so the token alone is not enough. On
   * API 35+ with a WebView older than 140, Capacitor pads the WebView's parent instead of letting
   * CSS inset, the WebView never reaches the status bar, and the strip is painted by the system
   * from `android:windowBackground` — light. Applying the primary contrast there puts white icons
   * on a white strip, which is worse than the bug this method fixes (observed on
   * `Pixel_6_API_36`).
   *
   * So branch on **who paints the background**, and use the inset as the signal — it already
   * encodes exactly that. A non-zero `--ion-safe-area-top` means the toolbar is painting through to
   * the top of the display (we published the height on API <= 34, or Capacitor passed real insets
   * through on a WebView >= 140); `0px` on Android means Capacitor padded us away from the bar and
   * the system owns those pixels, so defer to the device appearance, which is what
   * `windowBackground` follows too. iOS always draws under the status bar, so it always uses the
   * token.
   */
  private applyStatusBarStyleFromTheme(): void {
    if (!this.platformCapability.isMobileNative) {
      return;
    }
    try {
      const insetTop =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ion-safe-area-top')) || 0;

      // Step 1 — do we own those pixels at all? On Android a zero inset means Capacitor padded the
      // WebView clear of the status bar, so the strip is the system's `android:windowBackground`
      // and there is nothing in our DOM to measure. Sampling here would read our own toolbar and
      // confidently return the wrong answer.
      if (this.platformCapability.platform === 'android' && insetTop <= 0) {
        this.setStatusBarStyle(Style.Default);
        return;
      }

      // Step 2 — measure what is actually behind the strip. Do not infer it from the brand token:
      // that assumes a branded toolbar is always up there, which is false on every header-less page
      // (the first-run walkthrough put white glyphs on a near-white background that way).
      const background = this.sampleStatusBarBackground(insetTop);
      const channels = background?.match(/[\d.]+/g)?.map(Number);
      if (!channels || channels.length < 3) {
        this.setStatusBarStyle(Style.Default);
        return;
      }

      // Perceptual luminance of whatever paints the strip: dark background -> light glyphs.
      // Still design-system-driven, just one step further down: the colour sampled here *is* the
      // resolved token (--kz-primary via ion-toolbar, a surface token on a plain page), so a theme
      // switch changes the sample and the bar follows without knowing any token names.
      const luminance = channels[0] * 0.299 + channels[1] * 0.587 + channels[2] * 0.114;
      this.setStatusBarStyle(luminance < 150 ? Style.Dark : Style.Light);
    } catch (err) {
      // Cosmetic only — never let it break startup, navigation or a theme switch.
      console.warn('Could not derive the status bar style', err);
    }
  }

  /** Apply a status bar style, skipping the native round-trip when it is already in effect. */
  private setStatusBarStyle(style: Style): void {
    if (this.lastAppliedStatusBarStyle === style) {
      return;
    }
    this.lastAppliedStatusBarStyle = style;
    void StatusBar.setStyle({ style });
  }

  /** True when a computed `background-color` actually paints something. */
  private static isOpaqueColour(bg: string | undefined | null): boolean {
    if (!bg || bg === 'transparent') {
      return false;
    }
    return Number(bg.match(/[\d.]+/g)?.[3] ?? 1) > 0;
  }

  /**
   * The effective background colour behind the status bar strip, or null if nothing paints it.
   *
   * `elementFromPoint` returns the topmost *light-DOM* element, whose own background is usually
   * transparent, so walk ancestors. Two Ionic-specific traps make the naive walk wrong:
   *
   * 1. **Ionic paints inside its shadow roots.** An `<ion-toolbar color="primary">` host computes
   *    to `rgba(0,0,0,0)`; the brand colour lives on `.toolbar-background` in its shadow root. A
   *    light-DOM-only walk finds nothing all the way up to `ion-app` and gives up.
   * 2. **Do not read `--background` as a shortcut.** On that same primary toolbar it resolves to
   *    `#ffffff` — the unset default — because the `color` attribute paints by another route.
   *    Trusting it puts dark glyphs back on the purple header, i.e. the original bug.
   *
   * So: check each node's shadow children first, then the node itself, then fall back to `body`,
   * which is where the `--ion-background-color` surface token actually lands (`ion-content` is
   * transparent too, all the way down its shadow root).
   */
  private sampleStatusBarBackground(insetTop: number): string | null {
    const x = Math.round(window.innerWidth / 2);
    const y = insetTop > 0 ? Math.round(insetTop / 2) : 1;
    let node = document.elementFromPoint(x, y) as HTMLElement | null;

    while (node) {
      if (node.shadowRoot) {
        for (const child of Array.from(node.shadowRoot.children)) {
          const shadowBg = getComputedStyle(child).backgroundColor;
          if (AppComponent.isOpaqueColour(shadowBg)) {
            return shadowBg;
          }
        }
      }
      const bg = getComputedStyle(node).backgroundColor;
      if (AppComponent.isOpaqueColour(bg)) {
        return bg;
      }
      node = node.parentElement;
    }

    const bodyBg = getComputedStyle(document.body).backgroundColor;
    return AppComponent.isOpaqueColour(bodyBg) ? bodyBg : null;
  }

  /**
   * Re-measure after the view that owns the strip has actually painted.
   *
   * Navigation fires before the incoming page renders, so a synchronous sample would read the
   * outgoing one. Modals are the same problem without a route change — the item drawer swaps a
   * branded header into the strip — which is why `ionModalDidPresent`/`DidDismiss` are wired up
   * alongside the router.
   */
  private scheduleStatusBarRestyle(delayMs = 0): void {
    if (!this.platformCapability.isMobileNative) {
      return;
    }
    // Sample more than once. A single delay cannot be right for every transition: during an Ionic
    // page animation both pages are in the DOM, so an early sample can read the outgoing page or a
    // half-positioned layer — /login resolved to its white content area at 150ms and only settled
    // on the purple brand panel afterwards. The early passes keep the bar responsive, the late one
    // corrects a mid-transition misread, and `applyStatusBarStyleFromTheme` skips the native call
    // when nothing changed, so the extra passes are almost free.
    for (const delay of [delayMs, delayMs + 180, delayMs + 550]) {
      setTimeout(() => this.applyStatusBarStyleFromTheme(), delay);
    }
  }

  /**
   * Publish the Android status bar height as `--safe-area-inset-top`.
   *
   * The WebView is laid out behind the status bar (the StatusBar plugin defaults
   * `overlaysWebView` to true, and Android 15+ enforces edge-to-edge regardless), so the web
   * layer owes itself a top inset. It has no way to measure one on its own:
   * `env(safe-area-inset-top)` is 0px in the Android WebView unless it is Chrome >= 140 *and*
   * `viewport-fit=cover` is set, and Capacitor's built-in SystemBars plugin only publishes
   * `--safe-area-inset-*` from API 35 up (see the VANILLA_ICE_CREAM guards in SystemBars.java).
   * On anything older both sources read 0px, so `ion-header`'s
   * `padding-top: var(--ion-safe-area-top)` collapsed and every screen's header rendered
   * underneath the clock and status icons.
   *
   * `StatusBar.getInfo()` reports the real height (typed since plugin v7), so fill the gap with
   * it — deliberately writing the *same* variable name Capacitor uses, so `--ion-safe-area-top`
   * (themes/_contract.scss) has one contract to read from on every API level.
   *
   * Never overwrite a value Capacitor already published: on API 35+ its number is authoritative
   * and is correctly 0px when it has already padded the WebView's parent view itself. Writing
   * ours over it would double-inset.
   */
  private async publishAndroidStatusBarInset(): Promise<void> {
    try {
      const root = document.documentElement;
      const current = root.style.getPropertyValue('--safe-area-inset-top').trim();

      // Capacitor writes this as an inline style too, so "is it already set?" cannot tell the
      // two of us apart — compare against the last value we wrote instead. Anything else present
      // came from SystemBars (API 35+) and is authoritative: it is the real inset when the plugin
      // lets CSS do the insetting, and 0px when it has already padded the WebView's parent view.
      // Overwriting it would double-inset the header.
      if (current && current !== this.androidStatusBarInsetWritten) {
        return;
      }

      const { overlays, height } = await StatusBar.getInfo();
      if (overlays && height > 0) {
        this.androidStatusBarInsetWritten = `${height}px`;
        root.style.setProperty('--safe-area-inset-top', this.androidStatusBarInsetWritten);
      } else {
        // Not overlaid (or no measurable bar): fall back to the stylesheet's env() value.
        this.androidStatusBarInsetWritten = null;
        root.style.removeProperty('--safe-area-inset-top');
      }
      // The icon colour depends on whether the toolbar or the system paints the strip, which is
      // exactly what the inset above decides — so always restyle after resolving it.
      this.applyStatusBarStyleFromTheme();
    } catch (err) {
      // Never let a cosmetic inset break startup — a missing inset is the old behaviour, not a crash.
      console.warn('Could not resolve the Android status bar inset', err);
    }
  }

  async initializeApp() {

    try{

      this.platform.ready().then(() => {
        console.log('Platform ready');
        // iOS/Android only. `platform.is('capacitor')` is also true on Electron desktop,
        // where @capacitor/status-bar has no implementation and these calls throw.
        //
        // No setOverlaysWebView() call here on purpose. The plugin already defaults
        // `overlaysWebView` to true (StatusBarConfig.java) and applies it during load(), and from
        // Android 15 (API 35) the system enforces edge-to-edge anyway — Android 16 removed the
        // `windowOptOutEdgeToEdgeEnforcement` escape hatch entirely, so the setter is dead API on
        // the SDK we target. Calling it explicitly changed nothing; what was missing was the
        // matching top inset.
        if (this.platformCapability.isMobileNative) {
          if (this.platformCapability.platform === 'android') {
            // Resolves the inset first, then restyles from it — see publishAndroidStatusBarInset().
            void this.publishAndroidStatusBarInset();
          } else {
            this.applyStatusBarStyleFromTheme();
          }
        }
      });


      // Re-measure on rotation / multi-window resize, where the status bar height can change.
      window.addEventListener('resize', () => {
        if (this.platformCapability.platform === 'android') {
          void this.publishAndroidStatusBarInset();
        }
      });

      // What sits behind the status bar changes with the view, so restyle whenever the view does.
      this.statusBarRouteSubscription = this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe(() => this.scheduleStatusBarRestyle());

      // Modals are a view change without a navigation — the item drawer pushes a branded header
      // into the strip. Both events fire after their animation, so no extra settle time is needed.
      document.addEventListener('ionModalDidPresent', this.onModalStatusBarChange);
      document.addEventListener('ionModalDidDismiss', this.onModalStatusBarChange);

      this.platform.pause.subscribe(async () => {
        console.log('Pause event detected');
      });

      this.platform.resume.subscribe(async () => {
        console.log('Resume event detected');
      });

      // this.pushNotificationRegistrationService.initPush();
      
      //use: https://github.com/capawesome-team/capacitor-background-task
      //https://github.com/ionic-team/capacitor/issues/3032
      //https://forum.ionicframework.com/t/how-to-schedule-background-tasks/211058/5
       //https://capacitorjs.com/docs/apis/local-notifications#scheduleoptions
      // this.backgroundMode.on('activate').subscribe(() => {
      //   // LocalNotifications.schedule({
      //   //   title: 'koomzo app in background mode',
      //           body:''
      //   // });
        
      //   console.log('koomzo app in background mode');
      // });
      // this.backgroundMode.enable();

      

      // kz2 M12 (R11 G): the location lookup waits for the first page and an idle moment.
      void afterFirstNavigation(this.router)
        .then(() => this.locationService.getMyLocation({ quiet: true }))
        .then((loc:IGeoLocationModel)=>{
          if (loc) { this.store.dispatch(new SetUserLocation(loc)); }
        }).catch((err)=>{
          console.warn('getMyLocation: location not available', err);
        });


      //this.locationService.currentLocation$.subscribe(t=>)
      

      await SplashScreen.hide();

      //timer(3000).subscribe(() => (this.showSplash = false));

    
  } catch (err) {
    console.log('This is normal in a browser', err);
  }

      
  }

  togglePageState(p: IPage): void {
    p.open = !p.open;
  }

  ngAfterViewInit() {
    /* kzd r1 (§16 item 26): priority −1, and only when the outlet cannot go back — a screen that claims the back
       button (the locked kiosk) suppresses it. */
    this.backButtonSubscription = registerAppExit(
      this.platform.backButton,
      /* REVIEW-WI-rest-10 recheck N1: inside the provider app the page stack is the shell's nested outlet (the root
         stays at one entry), so the TOPMOST registered outlet is asked too — back never exits from the register. */
      () => !!this.routerOutlet?.canGoBack() || this.outlets.canGoBack(),
      () => navigator['app']?.exitApp?.(),
    );
  }

  ngOnDestroy() {
    this.routeLoadingSub.unsubscribe();
    if (this.routeLoadingTimer !== null) {
      clearTimeout(this.routeLoadingTimer);
    }
    this.locationService.canStartLocationTracking$.unsubscribe();
    if(this.locationService.currentLocationIsSubscribed){
      this.locationService.currentLocation$?.unsubscribe();
    }
    this.locationService.stopTracking();
    this.backButtonSubscription.unsubscribe();
    this.menuSubscription?.unsubscribe();
    this.statusBarRouteSubscription?.unsubscribe();
    document.removeEventListener('ionModalDidPresent', this.onModalStatusBarChange);
    document.removeEventListener('ionModalDidDismiss', this.onModalStatusBarChange);
  }



  logout(): void {
   // this.zone.run(() => this.authService.logout().then((()=>this.navCtrl.navigateRoot(['/login']))));
   this.store.dispatch(new Logout());
  }

  userProfile() {
    // Importing the navController result in an error while running, that's why here we go thru the app
    this.navCtrl.navigateForward(['/user-profile']);
  }

  /** The business's details: Settings opens its Business details sheet (the retired /tenants/edit page). */
  providerAccount() {
    this.navCtrl.navigateForward(['/settings'], { queryParams: { business: 1 } });
  }

  /**
   * Rehydrate localization settings (currency + payment channels) after a session
   * restore. The login form dispatches SetLocalizationSettings, but RestoreSession
   * (page reload) previously did not — leaving `state.app.currentLocalizationSettings`
   * null, which crashes the payment modal (MakePaymentComponent / OrderRecordDbService
   * read `.currency` unguarded). `restoreAuthSession` and the base-state load (which
   * populates `countryList`) are parallel APP_INITIALIZERs, so we wait for the
   * countryList before deriving the country from the restored user's mobile. No-op
   * if localization is already set (e.g. after an interactive login).
   */
  private rehydrateLocalizationOnRestore(signedIn = false): void {
    const user = this.currentUser;
    if (!user?.mobile && !user?.country) { return; }
    if (!signedIn && this.store.selectSnapshot<IAppStateModel>(state => state.app).currentLocalizationSettings) {
      return;
    }
    this.store
      .select((state: { app: IAppStateModel }) => state.app.countryList)
      .pipe(
        filter((list: any[]) => !!list?.length),
        take(1),
      )
      .subscribe((countryList: any[]) => {
        // An interactive login may have set it while we waited — re-check.
        if (!signedIn && this.store.selectSnapshot<IAppStateModel>(state => state.app).currentLocalizationSettings) {
          return;
        }
        // The stored country first (kz2 accounts), else the LONGEST calling code the number starts with.
        const code = user.country || countryOfPhone(user.mobile, countryList);
        if (code) {
          this.store.dispatch(new SetLocalizationSettings(code));
        }
      });
  }

  /**
   * Populate the nav menu for the current user. Shared by the LogIn/RestoreSession
   * action handler and the ngOnInit startup check (covers the timing race where a
   * session restore completes during APP_INITIALIZER, before ngOnInit subscribes).
   */
  private populateMenu(): void {
    const user = this.currentUser;
    if (!user) { return; }
    kzMark('shell:populate-menu');

    // Load the tenant's app selection — only for a member of a tenant (kz2 M11, walk F16): a consumer
    // has none, and the read was `where('tenantId', '==', undefined)`, which Firestore refuses.
    if (user.tenantId) {
      this.store.dispatch(new LoadTenantAppSelection({ id: user.tenantId }));
    }

    // kz2 F2: follow the tenant's capability document, resolved for this person's session role
    // (Q6). A user with no tenant (platform staff, consumers) has no capability document.
    if (user.tenantId) {
      this.store.dispatch(
        new LoadTenantCapabilities({ tenantId: user.tenantId, role: sessionRoleFor(user) ?? 'staff' }),
      );
    }

    // Rebuild the nav whenever the tenant's app selection resolves/changes. The load above is
    // async, so reading a one-off snapshot here would race it (empty selection ⇒ install-gate
    // skipped ⇒ every app shown). Selecting the slice re-emits once the load lands, re-gating
    // the nav to the installed apps. (Re-subscribe safely — populateMenu runs on login + init.)
    this.menuSubscription?.unsubscribe();
    // kz2 F2: the capability state re-gates the nav too, so a switch turned off in Setup (here or on
    // another device) takes its entry out of the nav live.
    // kz2 M12 (R11 E): and when the base state's pages change — the start-up may run on this device's
    // cached copy (or none, after a bounded wait) while the live document is still on its way.
    this.menuSubscription = combineLatest([
      this.store.select(state => state.app.tenantAppSelection as ITenantAppSelection | null),
      this.store.select(CapabilityNgxsState.caps),
      this.store.select(state => state.app.pages as IPage[]),
    ])
      .pipe(map(([selection]) => selection))
      .subscribe(selection => {
        this.tenantAppSelectionAppCodes = selection?.appCode ?? [];
        this.pages$ = this.getMenuPages(user);
        this.pages$.pipe(take(1)).subscribe(p => this.pagesSubject.next(p || []));
      });
  }

  private getMenuPages(currentUser?: IUser): Observable<IPage[]> {
    if (!currentUser) return of([]);
    if (environment.appMode === 'client') {
      return this.loadServiceConsumerPages(currentUser);
    }

    if (environment.appMode === 'server') {
      return this.loadServiceProviderPages(currentUser);
    }

    if (environment.appMode === 'all') {
      // The owner's rule (28 Sep 2026): every tenant member — staff too — gets the provider menu;
      // the consumer menu is for pure consumers only (`inProviderSpace`).
      if (inProviderSpace(currentUser)) {
        return this.loadServiceProviderPages(currentUser);
      } else {
        return this.loadServiceConsumerPages(currentUser);
      }
    }
  }

  loadServiceProviderPages(currentUser?: IUser): Observable<IPage[]> {
    let applicablePages: IPage[] = [];
    const storePages = this.store.selectSnapshot<IPage[]>(state => state.app.pages);
    // data from state engine is readonly/immutable  so need to create a new mutable object off this immutable state object
    let pages = storePages.map(p => new Page({ ...p }));

    // Install-gate: keep non-app pages, always-on SYSTEM apps (role-gated below), and the tenant's
    // installed apps. Applied even when the selection is EMPTY — so a tenant/owner with nothing
    // installed sees only Home / App Selector / system apps, not every app. populateMenu() dispatches
    // LoadTenantAppSelection and re-runs this on each emission, so installed apps appear once loaded.
    pages = pages.filter(p => p.appModuleId == undefined || p.isSystemApp || (p.isAppModule && p.appModuleId && this.tenantAppSelectionAppCodes.includes(p.appModuleId)));

    // kz2 F2, capability gate: an installed module whose tier is `off` leaves the nav, and a child
    // shows only while its capability resolves `on` (capability-gate.ts). Ungated entries pass.
    pages = pages.filter(p => !p.isAppModule || appModuleShown(this.capability, p.appModuleId));
    const gate = (list: IPage[]): IPage[] =>
      list
        .filter(cp => capabilityShown(this.capability, cp.capability))
        .map(cp => {
          if (cp.children) cp.children = gate(cp.children);
          return cp;
        });
    pages.forEach(p => {
      if (p.children) {
        p.children = gate(p.children);
      }
    });

    /* The role gate, at every depth. It used to stop at the first level, so a group with no roles of
       its own (Inventory's "Purchases & Sales", "Data Center") passed with all its manager-only
       children. A group left with no child the user may open leaves the menu too (kz2 staff menu). */
    const allowed = (list: IPage[]): IPage[] =>
      list.filter(cp => {
        if (!cp.isAdminNavPage(currentUser)) return false;
        if (!cp.children?.length) return true;
        cp.children = allowed(cp.children);
        return cp.children.length > 0;
      });

    applicablePages = pages.filter(p => {
      if (p.children) {
        p.children = allowed(p.children);
      }
      // Drop app-module nodes left with no accessible children (e.g. all role-gated out for this
      // user) — they'd otherwise render as a dead, non-expandable, non-navigable item.
      if (p.isAppModule && !(p.children && p.children.length)) {
        return false;
      }
      return p.isAdminNavPage(currentUser);
    });

    return of(applicablePages);
  }

  loadServiceConsumerPages(currentUser?: IUser): Observable<IPage[]> {
    let flattenedPages: IPage[] = [];
    const storePages = this.store.selectSnapshot<IPage[]>(state => state.app.pages);
    // data from state engine is readonly/immutable  so need to create a new mutable object off this immutable state object
    const pages = storePages.map(p => new Page({ ...p }));
    pages.forEach(p => {
      if (p.children) {
        p.children.forEach(cp => flattenedPages.push(cp));
      } else {
        flattenedPages.push(p);
      }
    });
    flattenedPages = flattenedPages.filter(p => p.isClientNavPage(currentUser));
    return of(flattenedPages);
  }

  /**
   * The rail's language (kz2 M12, the EN-under-FR race). `appLang$` changes as soon as `ChangeAppLang`
   * patches the state, BEFORE the new language's file has loaded, and ngstack's `get()` falls back to
   * English for a language with no data yet — so Home and Settings read English under French. The
   * rail rebuilds once more on `activeLangChanged`, which ngstack raises after the file has loaded
   * (as `KzLocaleService.lang()` follows it). No `distinctUntilChanged`: the second emission carries
   * the same language, and it is the one that finds the strings.
   */
  private railLang$(): Observable<string> {
    const loaded$ = this.translate.activeLangChanged
      ? this.translate.activeLangChanged.pipe(map(({ currentValue }) => currentValue as string))
      : EMPTY;
    return merge(this.appLang$.pipe(startWith(this.languageService.activeLang || 'en')), loaded$).pipe(
      filter((lang): lang is string => !!lang),
    );
  }

  /** Nav-rail click handler for non-route actions (logout); route items navigate via routerLink. */
  onNavItemClick(item: NavItem): void {
    if ((item?.data as { action?: string })?.action === 'logout') {
      this.logout();
    }
  }

  /** Map filtered pages -> NavItem[], with a Home entry on top and the fixed
      settings/logout footer actions at the bottom. */
  private buildNavItems(pages: IPage[], lang: string): NavItem[] {
    const items = (pages || [])
      .slice()
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map(p => this.pageToNavItem(p, lang));

    // Home shortcut at the very top (routes to the current role's home).
    items.unshift({ label: this.translate.get('uiString.home', undefined, lang), route: this.getRoleHomeRoute(), icon: 'home' });

    items.push({ label: this.translate.get('uiString.settings', undefined, lang), route: '/settings', icon: 'cog' });
    // Sign Out is intentionally NOT a nav-rail item — it lives as a child action inside Settings.
    return items;
  }

  /**
   * Role home route for the nav "Home" item: `roleHomeUrl` (kz2 M12, the guards' own target). For
   * providers the WORKSPACE home (`/provider/app/home`, the "Your apps" launcher) — NOT the bare
   * `/provider`, which the ProviderAppHomeRedirectGuard redirects to the tenant's default-app home.
   */
  private getRoleHomeRoute(): string {
    return roleHomeUrl(this.currentUser);
  }

  private pageToNavItem(p: IPage, lang: string, moduleId?: ModuleId | null): NavItem {
    // kz2 M1: the module a nav entry belongs to decides whose badges it can carry (D158).
    const mid = p.isAppModule ? moduleIdFor(p.appModuleId) : moduleId;
    const children = p.children?.length
      ? p.children
          .slice()
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map(c => this.pageToNavItem(c, lang, mid))
      : undefined;
    const badge = mid
      ? p.isAppModule
        ? this.attention.moduleBadgeFor(mid)()
        : p.url ? this.attention.badgesFor(mid)()[p.url] ?? null : null
      : null;
    // An app-module parent navigates to its OWN home (its leading "Home" child) when clicked;
    // pure group parents (Purchases & Sales, Data Center) have no route and only expand.
    const route = p.isAppModule && children?.length ? (children[0].route || p.url) : p.url;
    return {
      label: this.resolveTitle(p.title, lang),
      route,
      icon: p.icon,
      open: p.open,
      children,
      ...(badge ? { badge: attentionDisplay(badge.count), badgeTone: badge.tone } : {}),
    };
  }

  /** Mirror InlineTranslatePipe: resolve an ILocaleProp title for the active language. */
  private resolveTitle(title: any, lang: string): string {
    if (title == null) return '';
    if (typeof title === 'string') return title;
    return title[lang] ?? title['en'] ?? (Object.values(title)[0] as string) ?? '';
  }
}
